import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClientDto, CreateClientInput, Paginated } from '@cpm/shared';
import { Plus, Search, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client.js';
import { Modal } from '../components/Modal.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Spinner,
  Textarea,
  statusTone,
} from '../components/ui.js';

export function ClientForm({
  initial,
  onSubmit,
  pending,
  error,
  submitLabel,
}: {
  initial?: Partial<CreateClientInput>;
  onSubmit: (input: CreateClientInput) => void;
  pending: boolean;
  error: unknown;
  submitLabel: string;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [company, setCompany] = useState(initial?.company ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          name,
          company: company || null,
          email,
          phone: phone || null,
          notes: notes || null,
        });
      }}
      className="space-y-4"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="client-name">Name</Label>
          <Input id="client-name" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="client-company">Company</Label>
          <Input id="client-company" value={company} onChange={(e) => setCompany(e.target.value)} />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="client-email">Email</Label>
          <Input
            id="client-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="client-phone">Phone</Label>
          <Input id="client-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
      </div>
      <div>
        <Label htmlFor="client-notes">Notes</Label>
        <Textarea id="client-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      {error != null && <ErrorNote error={error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={pending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

export function ClientsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  const clients = useQuery({
    queryKey: ['clients', { search, page }],
    queryFn: () =>
      api.get<Paginated<ClientDto>>(
        `/api/clients/?page=${page}&perPage=25&search=${encodeURIComponent(search)}`,
      ),
  });

  const create = useMutation({
    mutationFn: (input: CreateClientInput) => api.post<ClientDto>('/api/clients/', input),
    onSuccess: (client) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      setCreateOpen(false);
      void navigate(`/clients/${client.id}`);
    },
  });

  return (
    <div>
      <PageHeader
        title="Clients"
        description="The people and companies you host for."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> New client
          </Button>
        }
      />

      <div className="mb-4 max-w-sm">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search clients…"
            className="pl-9"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <Card>
        {clients.isPending ? (
          <Spinner />
        ) : clients.error ? (
          <div className="p-5">
            <ErrorNote error={clients.error} />
          </div>
        ) : clients.data.items.length === 0 ? (
          <EmptyState
            icon={<Users className="size-10" />}
            title={search ? 'No clients match your search' : 'No clients yet'}
            description={
              search ? undefined : 'Create your first client to start assigning subscriptions.'
            }
            action={
              search ? undefined : (
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus className="size-4" /> New client
                </Button>
              )
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-3 font-medium">Client</th>
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Subscriptions</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {clients.data.items.map((client) => (
                  <tr
                    key={client.id}
                    className="cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    onClick={() => void navigate(`/clients/${client.id}`)}
                  >
                    <td className="px-5 py-3">
                      <Link
                        to={`/clients/${client.id}`}
                        className="font-medium hover:text-brand-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {client.name}
                      </Link>
                      {client.company && (
                        <p className="text-xs text-slate-500 dark:text-slate-400">{client.company}</p>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{client.email}</td>
                    <td className="px-5 py-3 tabular-nums">{client.subscriptionCount}</td>
                    <td className="px-5 py-3">
                      <Badge tone={statusTone(client.status)}>{client.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {clients.data.total > clients.data.perPage && (
              <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3 text-sm dark:border-slate-800">
                <span className="text-slate-500 dark:text-slate-400">
                  Page {clients.data.page} of {Math.ceil(clients.data.total / clients.data.perPage)}
                </span>
                <div className="flex gap-2">
                  <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={page >= Math.ceil(clients.data.total / clients.data.perPage)}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      <Modal title="New client" open={createOpen} onClose={() => setCreateOpen(false)} wide>
        <ClientForm
          onSubmit={(input) => create.mutate(input)}
          pending={create.isPending}
          error={create.error}
          submitLabel="Create client"
        />
      </Modal>
    </div>
  );
}
